import { localize } from "@/i18n/translate";
import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, FileStack, Inbox, LayoutGrid, LogOut, Menu, UserRound, Users } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { LangSwitch } from "@/i18n/LangContext";
import { Wordmark } from "@/components/site/Logo";
import { useAuth } from "./AuthContext";
import { usePortal } from "./i18n";
import { Spinner } from "./ui";
import { StatusScreen } from "./StatusScreen";
import { ForcePassword } from "./ForcePassword";
import { api } from "@/lib/api";
export const refreshUnread = () => window.dispatchEvent(new Event("sss:unread-refresh"));
const useUnread = admin => {
  const [count, setCount] = useState(0);
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    let alive = true;
    const get = () => api.get(admin ? "/admin/unread" : "/portal/unread").then(r => alive && setCount(r.data?.count ?? 0)).catch(() => {});
    get();
    const timer = setInterval(get, 30000);
    window.addEventListener("sss:unread-refresh", get);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener("sss:unread-refresh", get);
    };
  }, [admin, pathname]);
  return count;
};
const UnreadBadge = ({
  count,
  testId,
  admin
}) => {
  const {
    p
  } = usePortal();
  if (!count) return null;
  return <span data-testid={testId} className="ml-auto inline-flex min-w-[22px] items-center justify-center bg-signal px-1.5 py-0.5 font-mono text-xs font-medium text-white">
      <span aria-hidden="true">{localize(count)}</span>
      <span className="sr-only">{localize((admin ? p.unreadMsg : p.unread)(count))}</span>
    </span>;
};
const SideNav = ({
  admin,
  onNavigate,
  unread = 0
}) => {
  const {
    p
  } = usePortal();
  const {
    user,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const links = admin ? [{
    to: "/admin",
    label: p.nav.overview,
    icon: LayoutGrid,
    end: true
  }, {
    to: "/admin/workspace",
    label: p.nav.clients + " · Workspace",
    icon: LayoutGrid
  }, {
    to: "/admin/anfragen",
    label: p.nav.tickets,
    icon: Inbox,
    badge: true
  }, {
    to: "/admin/kunden",
    label: p.nav.clients,
    icon: Users
  }, {
    to: "/admin/kontaktanfragen",
    label: p.nav.contacts,
    icon: FileStack
  }] : [{
    to: "/kundenbereich",
    label: p.nav.overview,
    icon: LayoutGrid,
    end: true,
    badge: true
  }, {
    to: "/kundenbereich/anfragen",
    label: p.nav.tickets,
    icon: Inbox
  }, {
    to: "/kundenbereich/profil",
    label: p.nav.profile,
    icon: UserRound
  }];
  if (admin && user?.admin_level === 'owner') links.push({to: '/admin/site-editor', label: 'Редактор сайта', icon: LayoutGrid});
  if (admin && user?.admin_level === 'owner') links.push({to: '/admin/workspace-editor', label: 'Редактор кабинета', icon: LayoutGrid});
  const out = async () => {
    await logout();
    navigate("/kundenbereich/anmelden");
  };
  return <div className="on-dark flex h-full flex-col bg-ink p-6 text-paper">
      <Link to="/" onClick={onNavigate} aria-label={localize("Swiss Blockchain Alliance")} data-testid="portal-logo"><Wordmark dark /></Link>
      <p className="eyebrow mt-8 !text-zinc-400">{localize(admin ? p.admin : p.area)}</p>
      <nav className="mt-4 flex flex-col gap-1" aria-label={localize(admin ? p.admin : p.area)}>
        {localize(links.map(({
        to,
        label,
        icon: Icon,
        end,
        badge
      }) => <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`portal-nav-${to.split("/").pop() || "home"}`} className={({
        isActive
      }) => `flex items-center gap-3 px-3 py-3 text-sm font-medium transition-colors ${isActive ? "bg-paper text-ink" : "text-zinc-300 hover:bg-white/5 hover:text-white"}`}>
            <Icon className="h-4 w-4" aria-hidden="true" /> {localize(label)}
            {localize(badge && <UnreadBadge count={unread} admin={admin} testId="nav-unread-badge" />)}
          </NavLink>))}
      </nav>
      <div className="mt-auto space-y-4 pt-8">
        <div className="border-t border-white/15 pt-4">
          <p className="truncate text-sm font-semibold" data-testid="portal-user-name">{localize(user?.name)}</p>
          <p className="truncate text-xs text-zinc-400">{localize(user?.email)}</p>
        </div>
        <LangSwitch testPrefix="portal-lang" className="language-picker-dark" />
        <Link to="/" onClick={onNavigate} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white" data-testid="portal-back-site">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {localize(p.backToSite)}
        </Link>
        <button type="button" onClick={out} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white" data-testid="portal-logout">
          <LogOut className="h-4 w-4" aria-hidden="true" /> {localize(p.logout)}
        </button>
      </div>
    </div>;
};
const MobileBar = ({
  admin,
  unread = 0
}) => {
  const [open, setOpen] = useState(false);
  const {
    p
  } = usePortal();
  return <div className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-ink/10 bg-paper/90 px-5 backdrop-blur-xl lg:hidden">
      <Link to="/" aria-label={localize("Swiss Blockchain Alliance")}><Wordmark /></Link>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button type="button" className="relative inline-flex h-11 w-11 items-center justify-center border border-ink/20" aria-label={localize(unread ? `${p.menu} – ${(admin ? p.unreadMsg : p.unread)(unread)}` : p.menu)} data-testid="portal-menu-open">
            <Menu className="h-5 w-5" aria-hidden="true" />
            {localize(unread > 0 && <span aria-hidden="true" data-testid="mobile-unread-badge" className="absolute -right-2 -top-2 inline-flex min-w-[20px] items-center justify-center bg-signal px-1 py-0.5 font-mono text-[11px] text-white">{localize(unread)}</span>)}
          </button>
        </SheetTrigger>
        <SheetContent side="left" data-lenis-prevent className="w-[290px] border-0 p-0">
          <SheetTitle className="sr-only">{localize(admin ? p.admin : p.area)}</SheetTitle>
          <SheetDescription className="sr-only">{localize(p.nav.overview)}</SheetDescription>
          <SideNav admin={admin} unread={unread} onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
    </div>;
};
export const PortalShell = ({
  admin,
  children
}) => {
  const unread = useUnread(admin);
  return <div className="min-h-screen lg:grid lg:grid-cols-[272px_1fr]">
    <aside className="sticky top-0 hidden h-screen lg:block">
      <SideNav admin={admin} unread={unread} />
    </aside>
    <MobileBar admin={admin} unread={unread} />
    <div className="mx-auto w-full max-w-[1100px] px-5 py-10 sm:px-8 md:py-14 lg:px-12" id="portal-main">
      {localize(children)}
    </div>
  </div>;
};
export const RequireRole = ({
  role,
  bare = false,
  children
}) => {
  const {
    user
  } = useAuth();
  const {
    p
  } = usePortal();
  const location = useLocation();
  if (user === null) return <div className="mx-auto max-w-[1100px] px-5"><Spinner label={p.loading} /></div>;
  if (!user) return <Navigate to="/kundenbereich/anmelden" replace state={{
    from: location.pathname
  }} />;
  if (role === "admin" && user.role !== "admin") return <Navigate to="/kundenbereich" replace />;
  if (role === "client" && user.role === "admin") return <Navigate to="/admin" replace />;
  if (role === "client" && user.status !== "approved") return <StatusScreen />;
  if (role === "client" && user.must_change_password) return <ForcePassword />;
  return bare ? children : <PortalShell admin={role === "admin"}>{localize(children)}</PortalShell>;
};
